import axios from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { tableHeader } from "./MessageLogTableHeaders";
import config from './../config';

const InternalMessageLog = () => {

    const email1 = useSelector((state) => state.user.email);
    const navigate = useNavigate();
    const [messages, setMessages] = useState([])
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    
    // Filter states
    const [filters, setFilters] = useState({
        clientCode: "",
        dateFrom: "",
        dateTo: ""
    });

    // Date formatting function
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; // Return original if invalid date
            
            const day = date.getDate().toString().padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            
            return `${day}-${month}-${year}`;
        } catch (error) {
            return dateString; // Return original string if formatting fails
        }
    };

    /* useEffect(() => {
      if (email1 === null || email1 === "") {
        navigate("/")
      }
    }) */
    
    useEffect(() => {
        if (currentPage > 0 && pageSize > 0) {
            getInternalMessagesLog()
        }
    }, [currentPage, pageSize])

    const getInternalMessagesLog = async () => {
        setLoading(true);
        try {
            const searchCriteria = {
                ...filters,
                page: currentPage - 1, // API uses 0-based indexing
                size: pageSize,
                pageSize: pageSize, // Try both size and pageSize
                limit: pageSize     // Some APIs use limit instead of size
            };

            const res = await axios.post(config.BL_API_URL + "/messageLogs/v1/search", searchCriteria);

            if (res.data) {
                // Handle different possible response structures
                let messagesData = [];
                let total = 0;
                let pages = 0;

                if (res.data.content && res.data.pageInfo) {
                    // API response format: {content: [...], pageInfo: {...}}
                    messagesData = res.data.content;
                    total = res.data.pageInfo.totalElements || 0;
                    pages = res.data.pageInfo.totalPages || 0;
                } else if (res.data.content) {
                    // Spring Boot paginated response format
                    messagesData = res.data.content;
                    total = res.data.totalElements || 0;
                    pages = res.data.totalPages || 0;
                } else if (res.data.data) {
                    // Custom paginated response format
                    messagesData = res.data.data;
                    total = res.data.total || res.data.totalRecords || 0;
                    pages = res.data.totalPages || Math.ceil(total / pageSize);
                } else if (Array.isArray(res.data)) {
                    // Direct array response (non-paginated)
                    messagesData = res.data;
                    total = res.data.length;
                    pages = Math.ceil(total / pageSize);
                } else {
                    // Fallback for other structures
                    messagesData = res.data.messages || res.data.result || [];
                    total = res.data.totalElements || res.data.total || res.data.totalRecords || messagesData.length;
                    pages = res.data.totalPages || Math.ceil(total / pageSize);
                }

                // Client-side pagination fallback if API returns a full list instead of paged content
                if (messagesData.length > pageSize) {
                    const startIndex = (currentPage - 1) * pageSize;
                    const endIndex = startIndex + pageSize;
                    messagesData = messagesData.slice(startIndex, endIndex);
                }

                setMessages(messagesData);
                setTotalRecords(total);
                setTotalPages(pages);
            } else {
                setErr("backend error")
            }

        } catch (err) {
            // Fallback to original GET API if POST fails
            try {
                const res = await axios.get(config.BL_API_URL + "/messageLogs/v1/getlist");
                
                if (res.data && Array.isArray(res.data)) {
                    const allMessages = res.data;
                    const startIndex = (currentPage - 1) * pageSize;
                    const endIndex = startIndex + pageSize;
                    const paginatedMessages = allMessages.slice(startIndex, endIndex);
                    
                    setMessages(paginatedMessages);
                    setTotalRecords(allMessages.length);
                    setTotalPages(Math.ceil(allMessages.length / pageSize));
                } else {
                    setErr("No data received from server");
                }
            } catch (fallbackErr) {
                setErr("Server error...");
                if (fallbackErr?.response?.data) setErr(fallbackErr.response.data);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = () => {
        setCurrentPage(1); // Reset to first page when searching
        getInternalMessagesLog();
    };

    const handleClearFilters = () => {
        setFilters({
            clientCode: "",
            dateFrom: "",
            dateTo: ""
        });
        setCurrentPage(1);
        getInternalMessagesLog();
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        const newPageSize = parseInt(e.target.value);
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // Previous button
        buttons.push(
            <button
                key="prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1 mx-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
                Previous
            </button>
        );

        // Page number buttons
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    disabled={loading}
                    className={`px-3 py-1 mx-1 text-sm border rounded ${
                        currentPage === i 
                            ? 'bg-blue-500 text-white border-blue-500' 
                            : 'hover:bg-gray-100'
                    }`}
                >
                    {i}
                </button>
            );
        }

        // Next button
        buttons.push(
            <button
                key="next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="px-3 py-1 mx-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
                Next
            </button>
        );

        return buttons;
    };

    return (
        <div className="my-10 md:px-14 p-4 max-w-screen-2xl mx-auto py-5">
            
            
            {/* Filter Section */}
            <div className="border rounded-lg divide-y divide-gray-200 mb-3">
                <div className='col-span-10 flex gap-x-1 px-1 py-1 overflow-ellipsis'>
                    <div className="grid grid-cols-6 gap-2 justify-start border-0 border-red-500">
                        <div className="col-span-1 gap-5 text-xs">
                            <fieldset className='border-2 text-xs border-gray-200 p-0'>
                                <legend className='m-1 p-0'>Client Code</legend>
                                <input
                                    type="text"
                                    name="clientCode"
                                    value={filters.clientCode}
                                    onChange={handleFilterChange}
                                    placeholder="Enter client code"
                                    className="py-0 px-0 pe-0 block w-full focus:outline-none"
                                />
                            </fieldset>
                        </div>
                        <div className="col-span-1 gap-5 text-xs">
                            <fieldset className='border-2 text-xs border-gray-200 p-0'>
                                <legend className='m-1 p-0'>From Date</legend>
                                <input
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    className="py-0 px-0 pe-0 w-full block focus:outline-none"
                                />
                            </fieldset>
                        </div>
                        <div className="col-span-1 gap-5 text-xs">
                            <fieldset className='border-2 text-xs border-gray-200 p-0'>
                                <legend className='m-1 p-0'>To Date</legend>
                                <input
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    className="py-0 px-0 pe-0 w-full block focus:outline-none"
                                />
                            </fieldset>
                        </div>
                        <div className="col-span-1 gap-5 text-center text-xs">
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleSearch}
                                className="py-2 px-4 inline-flex right-0 gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-btnSecondary text-white hover:bg-btnPrimary disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {loading ? 'Searching...' : 'Search'}
                            </button>
                        </div>
                        <div className="col-span-1 gap-5 text-center text-xs">
                            <button
                                type="button"
                                onClick={handleClearFilters}
                                disabled={loading}
                                className="py-2 px-4 inline-flex right-0 gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-btnSecondary text-white hover:bg-btnPrimary disabled:opacity-50 disabled:pointer-events-none"
                            >
                                Clear Filters
                            </button>
                        </div>
                        <div className="col-span-1 gap-5 text-xs">
                            {/* Empty column for spacing */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Section with Pagination Controls */}
            <div className="overflow-hidden">
                {/* Pagination Controls - Top Right */}
                {totalRecords > 0 && (
                    <div className="mb-2 flex justify-end items-center">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">Show:</label>
                                <select
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                    disabled={loading}
                                    className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <span className="text-sm text-gray-700">records per page</span>
                            </div>
                            <div className="text-sm text-gray-700">
                                Total Records: {totalRecords}
                            </div>
                            <div className="text-sm text-gray-700">
                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
                            </div>
                            <div className="flex items-center">
                                {renderPaginationButtons()}
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="overflow-hidden">
                    <div className="relative overflow-x-auto">
                        <div className="max-h-96 overflow-y-scroll scrollbar-gutter-stable">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 bg-gray-200 uppercase">Basket Name</th>
                                        <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 bg-gray-200 uppercase">Client Code</th>
                                        <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 bg-gray-200 uppercase">Message Type</th>
                                        <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 bg-gray-200 uppercase">Message</th>
                                        <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 bg-gray-200 uppercase">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
                                            Loading messages...
                                        </div>
                                    </td>
                                </tr>
                            ) : messages && messages.length > 0 ? (
                                messages.map((item, index) => (
                                    <tr key={item.messageId || index} className={`${index % 2 === 0 ? '' : 'bg-gray-100'} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-200`}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{item.basketName || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{item.clientCode || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{item.messageType || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800 break-words">{item.message || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{formatDate(item.updatedOn)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center text-red-500">No records to display!</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            </div>

            {/* Error Display */}
            {err && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600">{err}</p>
                </div>
            )}
        </div>
        </div>
    )
}

export default InternalMessageLog